import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  TextField,
  Switch,
  FormControlLabel,
  FormGroup,
  Typography,
  Box,
  Divider,
  MenuItem,
} from '@mui/material';
import { format, parseISO, parse, isValid } from 'date-fns';
import { UPDATE_RESERVATION } from '../graphql/mutations';
import {
  formatDateForDisplay,
  formatTimeForDisplay,
  getTimeValue,
  combineDateAndTime
} from '../utils/timeUtils';

const EditReservationModal = ({ open, onClose, reservation, refetch, userRole }) => {
  // Debug the reservation object
  useEffect(() => {
    if (reservation) {
      console.log('Reservation data:', {
        id: reservation.id,
        check_in: reservation.check_in,
        check_out: reservation.check_out,
        check_in_time: reservation.check_in_time,
        check_out_time: reservation.check_out_time,
        override_check_in: reservation.override_check_in,
        override_check_out: reservation.override_check_out,
        has_time_override: reservation.has_time_override,
        email: reservation.email,
        mobile_phone: reservation.mobile_phone
      });
    }
  }, [reservation]);

  // Original reservation dates (read-only)
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  
  // Time fields (editable)
  const [checkInTime, setCheckInTime] = useState('15:00');  // Default 3:00 PM
  const [checkOutTime, setCheckOutTime] = useState('11:00'); // Default 11:00 AM
  
  // Override fields
  const [hasTimeOverride, setHasTimeOverride] = useState(false);
  const [overrideCheckInTime, setOverrideCheckInTime] = useState('15:00');
  const [overrideCheckOutTime, setOverrideCheckOutTime] = useState('11:00');
  
  // Add date override fields
  const [hasDateOverride, setHasDateOverride] = useState(false);
  const [overrideCheckInDate, setOverrideCheckInDate] = useState('');
  const [overrideCheckOutDate, setOverrideCheckOutDate] = useState('');
  
  const [error, setError] = useState('');

  const CHECKOUT_TIME_MIN = '10:00';
  const CHECKOUT_TIME_MAX = '16:00';

  const CHECKIN_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
    const hour24 = i.toString().padStart(2, '0');
    const hour12 = i === 0 ? 12 : i > 12 ? i - 12 : i;
    const period = i < 12 ? 'AM' : 'PM';
    return { value: `${hour24}:00`, label: `${hour12}:00 ${period}` };
  });

  const CHECKOUT_HOUR_OPTIONS = Array.from({ length: 7 }, (_, i) => {
    const hour24 = (i + 10).toString().padStart(2, '0');
    const hour12 = (i + 10) > 12 ? (i + 10) - 12 : (i + 10);
    const period = (i + 10) < 12 ? 'AM' : 'PM';
    return { value: `${hour24}:00`, label: `${hour12}:00 ${period}` };
  });

  // Helper function to safely parse time to 24-hour format
  const parseTimeToHHMM = (timeStr) => {
    if (!timeStr) return null;
    
    try {
      // If already in HH:mm format (24-hour)
      if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
        const [hours, minutes] = timeStr.split(':');
        const h = parseInt(hours, 10);
        // Validate it's actually 24-hour format (0-23)
        if (h >= 0 && h <= 23) {
          return `${hours.padStart(2, '0')}:${minutes}`;
        }
      }
      
      // Handle AM/PM format
      const amPmMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (amPmMatch) {
        let hours = parseInt(amPmMatch[1], 10);
        const minutes = amPmMatch[2];
        const period = amPmMatch[3].toUpperCase();
        
        if (period === 'PM' && hours < 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        
        return `${hours.toString().padStart(2, '0')}:${minutes}`;
      }
      
      console.warn('Could not parse time:', timeStr);
      return null;
    } catch (e) {
      console.error('Error parsing time:', e);
      return null;
    }
  };

  // Helper function to extract date from datetime string
  const extractDate = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    try {
      // Extract date directly from ISO string to avoid timezone conversion
      // Handle formats like: "2025-06-21T15:00:00.000Z" or "2025-06-21T15:00:00"
      const dateMatch = dateTimeStr.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        return dateMatch[1];
      }
      
      // Fallback to old method if regex fails
      const date = new Date(dateTimeStr);
      return format(date, 'yyyy-MM-dd');
    } catch (e) {
      return '';
    }
  };

  // Helper function to extract time from datetime string
  const extractTime = (dateTimeStr) => {
    if (!dateTimeStr) return null;
    try {
      // Extract time directly from ISO string to avoid timezone conversion
      // Handle formats like: "2025-06-21T15:00:00.000Z" or "2025-06-21T15:00:00"
      const timeMatch = dateTimeStr.match(/T(\d{2}):(\d{2}):(\d{2})/);
      if (timeMatch) {
        return `${timeMatch[1]}:${timeMatch[2]}`;
      }
      
      // Fallback to old method if regex fails
      const date = new Date(dateTimeStr);
      return format(date, 'HH:mm');
    } catch (e) {
      return null;
    }
  };

  const snapToHour = (time) => {
    if (!time) return time;
    const hour = time.split(':')[0];
    return `${hour}:00`;
  };

  // Update state when reservation prop changes - SIMPLIFIED
  useEffect(() => {
    if (reservation) {
      console.log('Initializing reservation data:', reservation);
      
      // Set original dates from the reservation
      const inDate = formatDateForDisplay(reservation.check_in);
      const outDate = formatDateForDisplay(reservation.check_out);
      setCheckInDate(inDate);
      setCheckOutDate(outDate);
      
      // Initialize override states
      const hasOverride = reservation.has_time_override || false;
      setHasTimeOverride(hasOverride);
      
      // Determine the current active times
      let currentCheckInTime, currentCheckOutTime;
      let overrideInTime, overrideOutTime;
      let overrideInDate = extractDate(reservation.check_in) || inDate;
      let overrideOutDate = extractDate(reservation.check_out) || outDate;
      let hasDateOverrideValue = false;
      
      if (hasOverride && (reservation.override_check_in || reservation.override_check_out)) {
        // If overrides exist, use those as the current active times
        if (reservation.override_check_in) {
          const extractedTime = extractTime(reservation.override_check_in);
          currentCheckInTime = parseTimeToHHMM(extractedTime) || '15:00';
          overrideInTime = currentCheckInTime;
          
          const extractedDate = extractDate(reservation.override_check_in);
          if (extractedDate && extractedDate !== extractDate(reservation.check_in)) {
            overrideInDate = extractedDate;
            hasDateOverrideValue = true;
          }
        } else {
          // Fallback to original if no override exists
          const rawInTime = getTimeValue(reservation.check_in_time, null);
          currentCheckInTime = parseTimeToHHMM(rawInTime) || '15:00';
          overrideInTime = currentCheckInTime;
        }
        
        if (reservation.override_check_out) {
          const extractedTime = extractTime(reservation.override_check_out);
          currentCheckOutTime = parseTimeToHHMM(extractedTime) || '11:00';
          overrideOutTime = currentCheckOutTime;
          
          const extractedDate = extractDate(reservation.override_check_out);
          if (extractedDate && extractedDate !== extractDate(reservation.check_out)) {
            overrideOutDate = extractedDate;
            hasDateOverrideValue = true;
          }
        } else {
          // Fallback to original if no override exists
          const rawOutTime = getTimeValue(reservation.check_out_time, null);
          currentCheckOutTime = parseTimeToHHMM(rawOutTime) || '11:00';
          overrideOutTime = currentCheckOutTime;
        }
      } else {
        // No overrides exist, use original times
        const rawInTime = getTimeValue(reservation.check_in_time, null);
        const rawOutTime = getTimeValue(reservation.check_out_time, null);
        
        currentCheckInTime = parseTimeToHHMM(rawInTime) || '15:00';
        currentCheckOutTime = parseTimeToHHMM(rawOutTime) || '11:00';
        overrideInTime = currentCheckInTime;
        overrideOutTime = currentCheckOutTime;
      }
      
      console.log('Processing times:', {
        hasOverride,
        override_check_in: reservation.override_check_in,
        override_check_out: reservation.override_check_out,
        check_in_time: reservation.check_in_time,
        check_out_time: reservation.check_out_time,
        currentCheckInTime,
        currentCheckOutTime,
        overrideInTime,
        overrideOutTime
      });
      
      // Set all time states
      setCheckInTime(currentCheckInTime);
      setCheckOutTime(currentCheckOutTime);
      setOverrideCheckInTime(snapToHour(overrideInTime));
      setOverrideCheckOutTime(snapToHour(overrideOutTime));
      setHasDateOverride(hasDateOverrideValue);
      setOverrideCheckInDate(overrideInDate);
      setOverrideCheckOutDate(overrideOutDate);
      
      console.log('Final initialized states:', {
        checkInTime: currentCheckInTime,
        checkOutTime: currentCheckOutTime,
        hasTimeOverride: hasOverride,
        hasDateOverride: hasDateOverrideValue,
        overrideCheckInTime: overrideInTime,
        overrideCheckOutTime: overrideOutTime,
        overrideCheckInDate: overrideInDate,
        overrideCheckOutDate: overrideOutDate
      });
    }
  }, [reservation]);

  const [updateReservation, { loading }] = useMutation(UPDATE_RESERVATION, {
    onCompleted: () => {
      refetch();
      onClose();
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  const handleToggleOverride = () => {
    const newValue = !hasTimeOverride;
    setHasTimeOverride(newValue);
    
    if (newValue) {
      const snappedIn = snapToHour(checkInTime);
      const snappedOut = snapToHour(checkOutTime);
      setOverrideCheckInTime(snappedIn);
      setOverrideCheckOutTime(snappedOut);
    }
  };
  
  const handleToggleDateOverride = () => {
    const newValue = !hasDateOverride;
    setHasDateOverride(newValue);
    
    // If enabling date override, initialize with original dates
    if (newValue) {
      setOverrideCheckInDate(extractDate(reservation?.check_in) || '');
      setOverrideCheckOutDate(extractDate(reservation?.check_out) || '');
    }
  };

  const handleSave = async () => {
    try {
      // Make sure we have the reservation
      if (!reservation || !reservation.id) {
        setError('Cannot update: Reservation data is missing');
        return;
      }

      // Get property ID, prioritizing property.id over property_id
      const propertyId = reservation.property?.id || reservation.property_id;
      
      console.log('Property reference for update:', {
        propertyObject: reservation.property,
        propertyId: propertyId,
        fromPropertyObject: reservation.property?.id,
        fromPropertyId: reservation.property_id
      });

      // Prepare the update variables
      let variables = {
        id: reservation.id,
        guest_name: reservation.guest_name,
        property_id: propertyId,
        email: reservation.email?.trim() || null,
        mobile_phone: reservation.mobile_phone?.trim() || null
      };

      // Validate required fields
      if (!variables.guest_name || !variables.property_id) {
        setError('Missing required fields: guest name or property');
        console.error('Missing fields:', {
          guest_name: variables.guest_name,
          property_id: variables.property_id,
          reservation: reservation
        });
        return;
      }

      // Determine if we need any kind of override
      const needsOverride = hasTimeOverride || hasDateOverride;

      // If we need overrides
      if (needsOverride) {
        // Validate required fields
        if (hasTimeOverride && (!overrideCheckInTime || !overrideCheckOutTime)) {
          setError('Both override check-in and check-out times are required');
          return;
        }
        
        if (hasTimeOverride && overrideCheckOutTime) {
          if (overrideCheckOutTime < CHECKOUT_TIME_MIN || overrideCheckOutTime > CHECKOUT_TIME_MAX) {
            setError('Check-out time must be between 10:00 AM and 4:00 PM to avoid shifting the reservation day');
            return;
          }
        }
        
        if (hasDateOverride && (!overrideCheckInDate || !overrideCheckOutDate)) {
          setError('Both override check-in and check-out dates are required');
          return;
        }

        // Get the dates to use (either overrides or originals)
        const finalCheckInDate = hasDateOverride ? overrideCheckInDate : extractDate(reservation.check_in);
        const finalCheckOutDate = hasDateOverride ? overrideCheckOutDate : extractDate(reservation.check_out);
        
        // Get the times to use (either overrides or originals)
        const finalCheckInTime = hasTimeOverride ? overrideCheckInTime : checkInTime;
        const finalCheckOutTime = hasTimeOverride ? overrideCheckOutTime : checkOutTime;

        console.log('Processing override times:', {
          finalCheckInDate,
          finalCheckOutDate,
          finalCheckInTime,
          finalCheckOutTime
        });

        // Parse times to ensure 24-hour format
        const parsedCheckInTime = parseTimeToHHMM(finalCheckInTime);
        const parsedCheckOutTime = parseTimeToHHMM(finalCheckOutTime);

        if (!parsedCheckInTime || !parsedCheckOutTime) {
          setError('Invalid time format. Please use HH:mm format');
          return;
        }

        if (!finalCheckInDate || !finalCheckOutDate) {
          setError('Invalid date format');
          return;
        }

        // Combine dates and times into ISO format with explicit UTC timezone
        const overrideCheckIn = `${finalCheckInDate}T${parsedCheckInTime}:00.000Z`;
        const overrideCheckOut = `${finalCheckOutDate}T${parsedCheckOutTime}:00.000Z`;

        console.log('Final override values:', {
          overrideCheckIn,
          overrideCheckOut,
          note: 'Times sent as UTC to prevent timezone conversion'
        });

        variables = {
          ...variables,
          has_time_override: true,
          override_check_in: overrideCheckIn,
          override_check_out: overrideCheckOut,
        };
      } else {
        // Reset override values if toggled off
        variables = {
          ...variables,
          has_time_override: false,
          override_check_in: null,
          override_check_out: null,
        };
      }

      console.log('Updating reservation with:', variables);

      // Perform the update
      await updateReservation({
        variables: {
          input: variables,
        },
      });
    } catch (err) {
      console.error('Error updating reservation:', err);
      setError(err.message || 'An error occurred while updating the reservation');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Reservation Date & Time</DialogTitle>
      <DialogContent>
        {/* Reservation Details */}
        <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200">
          <div className="font-semibold text-lg text-gray-800">
            {reservation?.property?.name || 'Reservation'}
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-medium">Guest:</span> {reservation?.guest_name}
          </div>
          {reservation?.email && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">Email:</span> {reservation.email}
            </div>
          )}
          {reservation?.mobile_phone && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">Phone:</span> {reservation.mobile_phone}
            </div>
          )}
        </div>
        <div className="space-y-4 mt-4">
          {error && (
            <Alert severity="error" className="mb-4">
              {error}
            </Alert>
          )}
          
          {/* Top: Original Check-in/Check-out (Styled) */}
          <div className="mb-6 flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div>
              <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Check-in</div>
              <div className="flex items-center space-x-2">
                {/* Calendar Icon */}
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                <span className="font-medium text-gray-800">{checkInDate}</span>
                {/* Clock Icon */}
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                <span className="text-gray-700">{formatTimeForDisplay(checkInTime)}</span>
              </div>
              </div>
              <div>
              <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Check-out</div>
              <div className="flex items-center space-x-2">
                {/* Calendar Icon */}
                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                <span className="font-medium text-gray-800">{checkOutDate}</span>
                {/* Clock Icon */}
                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                <span className="text-gray-700">{formatTimeForDisplay(checkOutTime)}</span>
              </div>
            </div>
          </div>

          {/* Overrides Section */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={hasTimeOverride}
                    onChange={handleToggleOverride}
                    color="primary"
                  />
                }
                label="Override Check-in/Check-out Times"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={hasDateOverride}
                    onChange={handleToggleDateOverride}
                    color="primary"
                  />
                }
                label="Override Check-in/Check-out Dates"
              />
            </FormGroup>

            {(hasTimeOverride || hasDateOverride) && (
              <div className="space-y-4 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  {hasDateOverride && (
                    <>
                    <div>
                        <div className="text-xs text-gray-500 mb-1">New Check-in Date</div>
                      <TextField
                        type="date"
                        value={overrideCheckInDate}
                        onChange={(e) => setOverrideCheckInDate(e.target.value)}
                        fullWidth
                        error={Boolean(error && !overrideCheckInDate)}
                      />
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 mb-1">New Check-out Date</div>
                      <TextField
                        type="date"
                          value={overrideCheckOutDate}
                          onChange={(e) => setOverrideCheckOutDate(e.target.value)}
                        fullWidth
                          error={Boolean(error && !overrideCheckOutDate)}
                      />
                    </div>
                    </>
                  )}
                  {hasTimeOverride && (
                    <>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">New Check-in Time</div>
                      <TextField
                        select
                        value={overrideCheckInTime}
                        onChange={(e) => setOverrideCheckInTime(e.target.value)}
                        fullWidth
                        error={Boolean(error && !overrideCheckInTime)}
                        helperText={formatTimeForDisplay(overrideCheckInTime)}
                      >
                        {CHECKIN_HOUR_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">New Check-out Time</div>
                      <TextField
                        select
                        value={overrideCheckOutTime}
                        onChange={(e) => {
                          const value = e.target.value;
                          setOverrideCheckOutTime(value);
                          setError('');
                        }}
                        fullWidth
                        error={Boolean(overrideCheckOutTime && (overrideCheckOutTime < CHECKOUT_TIME_MIN || overrideCheckOutTime > CHECKOUT_TIME_MAX))}
                        helperText="Allowed: 10:00 AM – 4:00 PM"
                      >
                        {CHECKOUT_HOUR_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                    </>
                  )}
                </div>
                {(hasDateOverride || hasTimeOverride) && (
                  <div className="mt-3 p-3 bg-blue-100 rounded border border-blue-200">
                    <div className="text-blue-700 text-sm">
                      <strong>Note:</strong> Overriding {[
                        hasTimeOverride && 'times',
                        hasDateOverride && 'dates'
                      ].filter(Boolean).join(' and ')} will affect all jobs associated with this reservation.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} className="text-gray-600">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading || (hasTimeOverride && overrideCheckOutTime && (overrideCheckOutTime < CHECKOUT_TIME_MIN || overrideCheckOutTime > CHECKOUT_TIME_MAX))}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditReservationModal; 